import { useMemo } from 'react';
import { useSettingsStore } from '@/stores';
import type { Item, ItemType } from '@perfin/shared';
import type { GroupBy } from '@/stores/uiStore';

interface GroupedItemListProps<T extends Item> {
  items: T[];
  groupBy: GroupBy;
  itemType?: ItemType;  // Needed for category name resolution
  renderItem: (item: T) => React.ReactNode;
  renderGroup?: (groupKey: string, items: T[]) => React.ReactNode;
}

// Priority display order
const priorityOrder: Record<string, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
  future: 4,
  unknown: 5,
};

// Status display labels
const statusLabels: Record<string, string> = {
  new: 'New',
  inProgress: 'In Progress',
  complete: 'Complete',
  draft: 'Draft',
  pending: 'Pending',
  final: 'Final',
  recurring: 'Recurring',  // Sentinel status for recurring items
};

// Priority display labels
const priorityLabels: Record<string, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  future: 'Future',
};

// Get date key for grouping
// Always falls back to createdAt if dueDate is not available
// Returns actual formatted date (e.g., "Mar 4, 2024")
const getDateKey = (item: Item): { key: string; timestamp: number } => {
  const anyItem = item as unknown as { dueDate?: { toDate: () => Date }; createdAt?: { toDate: () => Date } };

  let date: Date | null = null;

  if (anyItem.dueDate?.toDate) {
    date = anyItem.dueDate.toDate();
  } else if (anyItem.createdAt?.toDate) {
    date = anyItem.createdAt.toDate();
  }

  if (!date) return { key: 'No Date', timestamp: 0 };

  // Return formatted date (e.g., "Mar 4, 2024")
  const formatted = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return { key: formatted, timestamp: date.getTime() };
};

// Get status key for grouping
const getStatusKey = (item: Item): string => {
  const anyItem = item as unknown as { status?: string };
  return anyItem.status || 'unknown';
};

// Get priority key for grouping
const getPriorityKey = (item: Item): string => {
  return item.priority || 'medium';
};

// Get owner key for grouping (returns raw ID, display name resolved later)
const getOwnerKey = (item: Item): string => {
  return item.owner || 'Unassigned';
};

// Get category key for grouping (returns category ID, display name resolved later)
const getCategoryKey = (item: Item): string => {
  return item.category || 'Uncategorized';
};

// Get itemGroup composite key for grouping: "Category > SubCategory > ItemGroup"
const getItemGroupKey = (item: Item): string => {
  const anyItem = item as unknown as { itemGroup?: string };
  const category = item.category || 'None';
  const subCategory = item.subCategory || 'None';
  const itemGroup = anyItem.itemGroup || 'None';
  return `${category} > ${subCategory} > ${itemGroup}`;
};

export function GroupedItemList<T extends Item>({
  items,
  groupBy,
  itemType,
  renderItem,
  renderGroup,
}: GroupedItemListProps<T>) {
  const { members, currentUserId, getCategoriesForType, categories } = useSettingsStore();

  const groupedItems = useMemo(() => {
    // Helper to get owner display name (defined inside useMemo to avoid dependency issues)
    const getOwnerDisplayName = (ownerId: string): string => {
      if (ownerId === 'Unassigned') return 'Unassigned';
      if (ownerId === currentUserId) return 'Me';
      const member = members.find((m) => m.userId === ownerId);
      return member?.displayName || member?.email || ownerId;
    };

    // Helper to get category display name
    const getCategoryDisplayName = (categoryId: string): string => {
      if (categoryId === 'Uncategorized') return 'Uncategorized';
      // Use itemType if provided, otherwise try to infer from first item
      const type = itemType || (items.length > 0 ? items[0].type : 'task');
      const typeCategories = getCategoriesForType(type);
      const category = typeCategories.find(c => c.id === categoryId);
      // Fallback to all categories for existing items with mismatched category types
      if (!category) {
        const fallbackCategory = categories.find(c => c.id === categoryId);
        return fallbackCategory?.name || categoryId;
      }
      return category.name;
    };

    const groups: Record<string, { items: T[]; timestamp: number }> = {};

    items.forEach((item) => {
      let key: string;
      let timestamp = 0;

      switch (groupBy) {
        case 'date': {
          const dateResult = getDateKey(item);
          key = dateResult.key;
          timestamp = dateResult.timestamp;
          break;
        }
        case 'category':
          key = getCategoryKey(item);
          break;
        case 'status':
          key = getStatusKey(item);
          break;
        case 'priority':
          key = getPriorityKey(item);
          break;
        case 'owner':
          key = getOwnerKey(item);
          break;
        case 'itemGroup':
          key = getItemGroupKey(item);
          break;
        default:
          key = 'Other';
      }

      if (!groups[key]) {
        groups[key] = { items: [], timestamp };
      }
      groups[key].items.push(item);
    });

    // Sort groups based on groupBy type
    const sortedEntries = Object.entries(groups).sort(([aKey, aData], [bKey, bData]) => {
      if (groupBy === 'priority') {
        return (priorityOrder[aKey] ?? 5) - (priorityOrder[bKey] ?? 5);
      }
      if (groupBy === 'date') {
        // Sort chronologically by timestamp
        return aData.timestamp - bData.timestamp;
      }
      if (groupBy === 'category') {
        // Alphabetical, but 'Uncategorized' last
        if (aKey === 'Uncategorized') return 1;
        if (bKey === 'Uncategorized') return -1;
        return aKey.localeCompare(bKey);
      }
      if (groupBy === 'itemGroup') {
        // Alphabetical sort for itemGroup composite keys
        return aKey.localeCompare(bKey);
      }
      return aKey.localeCompare(bKey);
    });

    // Helper to build itemGroup display label from composite key
    const getItemGroupDisplayLabel = (compositeKey: string): string => {
      // compositeKey format: "categoryId > subCategoryId > itemGroupName"
      const parts = compositeKey.split(' > ');
      if (parts.length !== 3) return compositeKey;

      const [categoryId, subCategoryId, itemGroupName] = parts;

      // Resolve category name
      const categoryName = categoryId === 'None' ? 'None' : getCategoryDisplayName(categoryId);

      // Resolve subcategory name
      let subCategoryName = subCategoryId;
      if (subCategoryId !== 'None' && categoryId !== 'None') {
        const type = itemType || (items.length > 0 ? items[0].type : 'task');
        const typeCategories = getCategoriesForType(type);
        const category = typeCategories.find(c => c.id === categoryId);
        const subCategory = category?.subCategories?.find(s => s.id === subCategoryId);
        subCategoryName = subCategory?.name || subCategoryId;
      }

      return `${categoryName} > ${subCategoryName} > ${itemGroupName}`;
    };

    return sortedEntries.map(([key, data]) => {
      let displayLabel = key;
      if (groupBy === 'status') {
        displayLabel = statusLabels[key] || key;
      } else if (groupBy === 'priority') {
        displayLabel = priorityLabels[key] || key;
      } else if (groupBy === 'owner') {
        displayLabel = getOwnerDisplayName(key);
      } else if (groupBy === 'category') {
        displayLabel = getCategoryDisplayName(key);
      } else if (groupBy === 'itemGroup') {
        displayLabel = getItemGroupDisplayLabel(key);
      }
      return { key, items: data.items, displayLabel };
    });
  }, [items, groupBy, itemType, members, currentUserId, getCategoriesForType, categories]);

  if (renderGroup) {
    return (
      <>
        {groupedItems.map(({ key, items: groupItems, displayLabel }) => (
          <div key={key}>
            {renderGroup(displayLabel, groupItems)}
          </div>
        ))}
      </>
    );
  }

  return (
    <div className="space-y-6">
      {groupedItems.map(({ key, items: groupItems, displayLabel }) => (
        <div key={key}>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 px-4">
            {displayLabel} ({groupItems.length})
          </h3>
          <div className="space-y-0">
            {groupItems.map((item) => renderItem(item))}
          </div>
        </div>
      ))}
    </div>
  );
}
